import {
  Activity,
  AtSign,
  BookOpen,
  Building2,
  Filter,
  LogOut,
  LayoutGrid,
  Mail,
  Menu,
  Monitor,
  Moon,
  PanelLeft,
  Plus,
  Sun,
  PlugZap,
  Tags,
  type LucideIcon,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { CampaignsMark } from '@/components/HazcheckMark';
import { useTheme, type ThemePreference } from '@/theme/use-theme';
import { signOut, Roles, type Session } from '@/auth/keycloak';
import { cn } from '@/lib/utils';
import { getConfig } from '@/lib/config';

/**
 * The application chrome: a topbar spanning the full width, and beneath it ONE ink rail holding
 * every destination, at whichever of its two widths the person last chose — 56px of icons, or 232px
 * of labelled rows.
 *
 * ── WHAT THIS REPLACED, AND WHY ───────────────────────────────────────────────────
 *
 * This was two columns: a 56px rail of three SECTIONS (Workspace, Administration, User guide) plus a
 * 208px column of the selected section's destinations. That shape came from the Compass board-portal,
 * whose own comment records why *it* exists — it replaced a single sidebar listing every destination,
 * because for an admin that column never fit without scrolling.
 *
 * Campaigns' destinations all fit one panel with room to spare — there are eight, and the panel holds
 * comfortably more — so the section layer was costing 264px of permanent width and a click (pick the
 * section, then pick the row) to solve a problem this application does not have. The sections survive
 * as GROUP CAPTIONS instead.
 *
 * ── NARROW OR FULL IS A REMEMBERED CHOICE, NOT A HOVER ────────────────────────────
 *
 * The panel toggle in the rail's top row switches between the two widths and the choice persists
 * (`campaigns.navExpanded`). This is the ChatGPT/VS Code model, and it replaced a pin plus
 * hover-peek, which read as the same thing but behaved differently: peek meant the panel opened
 * whenever the pointer crossed the rail, which is precisely what somebody who chose the narrow rail
 * was trying to stop. An explicit remembered toggle and an open-on-hover panel are contradictory
 * answers to the same question, so there is now no hover-peek and no `focus-within` expansion.
 *
 * Two things fell out of dropping peek, both good: the labels no longer need `group-hover` plumbing
 * (the state drives them directly), and the toggle no longer needs to blur itself after a pointer
 * click — that existed only because `focus-within` held the panel open and made the click look inert.
 *
 * Narrow rows keep their `title` AND their label text in the accessibility tree — `opacity-0` hides
 * ink, not semantics — so a screen reader still announces "Contacts" on a 56px rail.
 *
 * ── ONLY THE WIDTH MOVES ──────────────────────────────────────────────────────────
 *
 * Two rules, and both are load-bearing rather than polish:
 *
 * 1. A caption's height is RESERVED whether or not it is legible (`h-3.5` always, opacity toggling).
 *    Collapsing it to zero and back reflows every row beneath it *while* the width is animating, so
 *    opening the menu slid seven rows down the rail. Row positions are now identical in both states
 *    and width is the only thing in motion. Collapsed, the reserved strips read as group spacing.
 *
 * 2. Labels fade in 90ms LATE and out with no delay — see TEXT_SHOWN / TEXT_HIDDEN below.
 *
 * ── THE ≡ IS A PHONE CONTROL, THE PANEL TOGGLE IS A DESKTOP ONE ───────────────────
 *
 * On a desktop the rail is always on screen and its width is the panel toggle's job, so a topbar ≡
 * would be a second control for one job. Dropping it frees the topbar's left 56px — exactly the
 * rail's width — which the mark now occupies, centred, so it sits above the column of icons. That
 * is also why the topbar spans the FULL width now rather than starting to the right of the rail, and
 * why the old `RAIL_OFFSET` is gone: the rail begins below the topbar, so the first icon's active
 * tile can no longer run into it and read as a notch cut out of it.
 *
 * ── WHY THE RAIL'S ACTIVE TILE IS NOT A SURFACE TOKEN ─────────────────────────────
 *
 * Unchanged, and still true. `--surface` is `#102941` in dark, which is the rail's own ink: a tile
 * painted with it measured 1.1:1 and disappeared. No rung of the surface ladder works either, since
 * they are all within 1.4:1 of the rail once everything is dark. So the tile is FIXED white with
 * fixed blue ink in both themes, on the same reasoning as `--ink-*`: the rail is brand chrome and
 * brand chrome does not invert. The fixed white tile stays distinct in both themes.
 *
 * The teal bar to its left is a separate signal and is not about dark mode. Marking the active row by
 * fill alone is hue-only, which `ui-ux.md` § 7 forbids; a bar is a change of shape that survives any
 * ground.
 */

/** Shared by the topbar's height and the mark's slot. A disagreement is instantly visible. */
const TOPBAR_H = 'h-14';

/**
 * The rail's two widths, written as literal class strings on purpose.
 *
 * Tailwind scans source TEXT for candidates, so a class assembled at runtime — `` `md:${WIDTH}` ``
 * — is never generated and silently does nothing. Every variant that is used has to appear here
 * spelled out. `w-14` is 56px narrow; `w-58` is 232px full.
 */
const RAIL_WIDTH_FULL = 'w-58';
const RAIL_WIDTH_NARROW = 'md:w-14';

/**
 * Text that exists only in the full width: labels, group captions, the action's wording.
 *
 * The delay is asymmetric, and that is the point. A CSS transition uses the delay declared on the
 * state it is moving TO, so text fades IN 90ms late — half the 180ms slide, by which point there is
 * width to hold it — and fades OUT immediately, before the panel is too narrow for it. Without the
 * lag, text appears at 90px of width and is clipped by the panel's own overflow, which reads as the
 * letters unfolding sideways rather than the panel arriving.
 *
 * Only from `md` up. The phone drawer is always the full width, so it is always labelled.
 */
const TEXT_SHOWN = 'transition-opacity duration-150 md:delay-[90ms]';
const TEXT_HIDDEN = 'transition-opacity duration-150 md:opacity-0 md:delay-0';

type Destination = {
  to: string;
  label: string;
  Icon: LucideIcon;
  group: string;
};

/**
 * Every destination, under three captions.
 *
 * Deliberately NOT role-filtered. Reads on brands, senders and topics are open to every role — only
 * the writes are `Admin` (see `BrandsController`) — and those pages already render read-only for
 * everyone else. Hiding Administration from an Author would take away a list they are entitled to
 * see. The one thing that IS gated is the promoted New campaign action, which matches
 * `Roles.AuthoringRoles` on the server.
 */
const DESTINATIONS: Destination[] = [
  { to: '/campaigns', label: 'Campaigns', Icon: Mail, group: 'Workspace' },
  { to: '/contacts', label: 'Contacts', Icon: Users, group: 'Workspace' },
  { to: '/segments', label: 'Segments', Icon: Filter, group: 'Workspace' },
  { to: '/deliverability', label: 'Deliverability', Icon: Activity, group: 'Workspace' },

  { to: '/settings/brands', label: 'Brands', Icon: Building2, group: 'Administration' },
  { to: '/settings/senders', label: 'Sender addresses', Icon: AtSign, group: 'Administration' },
  { to: '/settings/topics', label: 'Topics', Icon: Tags, group: 'Administration' },

  // The exception to the note above: this one IS effectively admin-only, and the page says so to
  // everybody else rather than vanishing. Hiding it would be the wrong shape — an Author who has
  // been told "the SendGrid key is wrong" needs to know where that is checked and who can check it,
  // which is more useful than a missing menu entry.
  { to: '/settings/integrations', label: 'Integrations', Icon: PlugZap, group: 'Administration' },

  { to: '/user-guide', label: 'User guide', Icon: BookOpen, group: 'Help' },
];

const THEME_OPTIONS: { value: ThemePreference; label: string; Icon: LucideIcon }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'auto', label: 'System', Icon: Monitor },
];

/**
 * Renamed from `campaigns.navPinned` rather than migrated. The pin shipped on an unpushed branch and
 * has never been deployed, so the only stored values are from local testing — carrying a
 * compatibility read for those would be plumbing for nobody. A key called `navPinned` holding an
 * "is it full width" flag is exactly the stale name that misleads a reader later.
 */
const NAV_WIDTH_KEY = 'campaigns.navExpanded';

/** `CampaignsAuthor` → `Author`. The realm is shared, so the prefix is on every role name. */
function shortRoleName(role: string): string {
  return role.startsWith('Campaigns') ? role.slice('Campaigns'.length) : role;
}

function initials(name: string | null): string {
  const parts = (name ?? '').split(' ').filter((part) => part.length > 0);

  if (parts.length === 0) return '?';

  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}

export function AppShell({ session }: { session: Session }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [isDrawerOpen, setDrawerOpen] = useState(false);

  // Full width or narrow, remembered. Both are resting states — neither is a transient reveal.
  const [isExpanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem(NAV_WIDTH_KEY) !== '0';
    } catch {
      // Private mode throws. Full is the safe default: it is the one that hides nothing.
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(NAV_WIDTH_KEY, isExpanded ? '1' : '0');
    } catch {
      // Nothing to do — the preference just will not survive a reload.
    }
  }, [isExpanded]);

  // Close the drawer on navigation. Without this, picking a destination leaves the drawer you picked
  // it from sitting open over the page you just asked for.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Escape closes the drawer. It covers the page behind a scrim, so it has to be dismissible without
  // hitting a specific target — synchronising with a document-level event, hence an effect.
  useEffect(() => {
    if (!isDrawerOpen) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setDrawerOpen(false);
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDrawerOpen]);

  const canAuthor = session.roles.includes(Roles.Admin) || session.roles.includes(Roles.Author);

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-bg">
      {/* ── Topbar. Spans the FULL width, including over the rail, so the mark can align to it. ── */}
      <header
        className={cn(
          // Above the rail, so the account menu is not painted over by a panel that comes later in
          // the document.
          'z-50 flex shrink-0 items-center border-b border-border bg-surface pr-3',
          TOPBAR_H,
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          className="ml-2 md:hidden"
          onClick={() => setDrawerOpen((open) => !open)}
          aria-expanded={isDrawerOpen}
          aria-label={isDrawerOpen ? 'Close navigation' : 'Open navigation'}
        >
          {isDrawerOpen ? (
            <X className="size-5" aria-hidden="true" />
          ) : (
            <Menu className="size-5" aria-hidden="true" />
          )}
        </Button>

        {/* The mark once, here. "Hazcheck" regular and the product bold — one lockup, two weights,
            which is how the family reads as a family without a second logo per product.

            No `gap` on this row: the mark's 56px slot supplies the space to the wordmark, and a gap
            would push the wordmark out and break the alignment the slot exists to create. */}
        <span className="flex shrink-0 items-center">
          <span className={cn('grid shrink-0 place-items-center px-2 md:px-0', 'md:w-14')}>
            <CampaignsMark className="size-[30px] shrink-0" />
          </span>

          <span className="text-base tracking-tight text-heading">
            Hazcheck <b className="font-bold">Campaigns</b>
          </span>
        </span>

        <div className="ml-auto flex items-center gap-3">
          {/* Below md this folds into the account menu — three 32px buttons plus an avatar do not
              fit beside the wordmark at 375px, and 32px was already under the 44x44 touch minimum
              in ui-ux.md § 7. The menu's rows are full-width, so they clear it. */}
          <ThemeControl className="hidden md:flex" />

          <AccountMenu session={session} />
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1">
        {/* Inert to assistive technology on purpose. As a labelled button it is a SECOND control
            called "Close navigation" alongside the ≡, and two controls with one accessible name is
            worse than one — the keyboard already has Escape and the ≡ itself. */}
        {isDrawerOpen && (
          <div
            className="absolute inset-0 z-30 bg-black/40 md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* ── The rail. One element, three regimes: off-canvas drawer on a phone, and narrow or full
               on a desktop, whichever the person last chose. ── */}
        <nav
          id="app-navigation"
          aria-label="Main navigation"
          className={cn(
            'absolute inset-y-0 left-0 z-40 flex flex-col overflow-hidden',
            'border-r border-rail-divider bg-rail pb-2.5 pt-2 text-rail-ink',
            'transition-[width,transform] duration-200',

            // Mobile-first: the drawer. Written as the base with `md:` overrides rather than
            // `max-md:` overrides, so nothing depends on Tailwind's variant ordering.
            RAIL_WIDTH_FULL,
            'shadow-lg md:shadow-none',
            isDrawerOpen ? 'visible translate-x-0' : 'invisible -translate-x-full',
            'md:visible md:translate-x-0',

            isExpanded ? RAIL_WIDTH_FULL : RAIL_WIDTH_NARROW,
          )}
        >
          {/* pr-[15px] rather than a plain `pr-3`, so that narrow the 26px toggle's centre lands on
              28px and it reads as centred in the 56px rail — while still sitting at the panel's
              right edge when full. Both are where ChatGPT and VS Code put it, which is the point:
              this control is a convention, and the value of a convention is not reinventing it. */}
          <div className="flex h-7 shrink-0 items-center justify-end pr-[15px]">
            <button
              type="button"
              onClick={() => setExpanded(!isExpanded)}
              aria-expanded={isExpanded}
              aria-controls="app-navigation"
              title={isExpanded ? 'Narrow the sidebar' : 'Widen the sidebar'}
              aria-label={isExpanded ? 'Narrow the sidebar' : 'Widen the sidebar'}
              className={cn(
                'hidden size-6.5 shrink-0 place-items-center rounded transition-colors md:grid',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                // The SAME glyph in both states, unfilled, as in the reference. It names the thing
                // it acts on — the panel — rather than the direction it will move, so there is no
                // second icon to keep in step with the state.
                'text-rail-ink-muted hover:bg-white/10 hover:text-white',
              )}
            >
              <PanelLeft className="size-4" aria-hidden="true" />
            </button>
          </div>

          {/* The promoted action. Authoring roles only — a Sender or an Analyst cannot create one, so
              offering it would be a button that 403s. An icon square collapsed, a full button open. */}
          {canAuthor && (
            <button
              type="button"
              onClick={() => void navigate('/campaigns?new=1')}
              title="New campaign"
              className={cn(
                'mb-2 ml-2.5 mr-2 mt-0.5 flex h-9 shrink-0 items-center gap-2.5 rounded-md px-[9px]',
                'bg-primary-fill text-[13.5px] font-semibold whitespace-nowrap text-primary-on-fill',
                'hover:bg-primary-fill-hover',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
              )}
            >
              <Plus className="size-[18px] shrink-0" aria-hidden="true" />
              <span className={cn('min-w-0', isExpanded ? TEXT_SHOWN : TEXT_HIDDEN)}>
                New campaign
              </span>
            </button>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {DESTINATIONS.map((destination, index) => {
              const isNewGroup = DESTINATIONS[index - 1]?.group !== destination.group;

              return (
                <div
                  key={destination.to}
                  className={cn(
                    // 10px left, not 8: collapsed, the icon centre has to land on 28px, and
                    // 10 + 9 (row padding) + 9 (half an 18px icon) = 28.
                    'flex flex-col gap-0.5 pl-2.5 pr-2',
                    isNewGroup && 'mt-2 border-t border-rail-divider pt-2',
                  )}
                >
                  {isNewGroup && (
                    // h-3.5 ALWAYS — see the note at the top of this file. This is what stops the
                    // panel jumping when it opens.
                    <span
                      className={cn(
                        'block h-3.5 overflow-hidden px-2.5 text-[11px] font-semibold',
                        'leading-[14px] whitespace-nowrap text-rail-ink-muted',
                        isExpanded ? TEXT_SHOWN : TEXT_HIDDEN,
                      )}
                    >
                      {destination.group}
                    </span>
                  )}

                  <NavLink
                    to={destination.to}
                    title={destination.label}
                    className={({ isActive }) =>
                      cn(
                        'relative flex h-9 shrink-0 items-center gap-2.5 rounded-md px-[9px]',
                        'text-[13.5px] font-medium whitespace-nowrap transition-colors',
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                        isActive
                          ? // Literals under the brand-chrome exception in ui-ux.md § 1 — see the
                            // file header for why no theme token can be both legible on navy and
                            // stable across themes.
                            'bg-rail-tile font-semibold text-rail-tile-ink'
                          : 'text-rail-ink hover:bg-white/10 hover:text-white',
                        // The bar: a change of SHAPE, so the active row is not signalled by hue
                        // alone. -left-2.5 puts it on the rail's own edge.
                        isActive &&
                          'before:absolute before:inset-y-1.5 before:-left-2.5 before:w-[3px] before:rounded-r-full before:bg-rail-accent',
                      )
                    }
                  >
                    <destination.Icon className="size-[18px] shrink-0" aria-hidden="true" />
                    <span className={cn('min-w-0 truncate', isExpanded ? TEXT_SHOWN : TEXT_HIDDEN)}>
                      {destination.label}
                    </span>
                  </NavLink>
                </div>
              );
            })}
          </div>

          <a
            href={getConfig().hubUrl}
            title="HazcheckHub"
            className="mx-2.5 mt-2 flex min-h-11 shrink-0 items-center gap-2.5 border-t border-rail-divider px-[9px] text-[13.5px] text-rail-ink hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <LayoutGrid className="size-[18px] shrink-0" aria-hidden="true" />
            <span className={cn('whitespace-nowrap', isExpanded ? TEXT_SHOWN : TEXT_HIDDEN)}>
              HazcheckHub
            </span>
          </a>

          {/* Phone only. On a desktop this lives in the account menu; on a phone the drawer is the
              only navigation there is, and hiding the way out behind an avatar in a crowded topbar
              is worse. Behind a divider at the bottom, away from the destinations, so a mis-tap
              while navigating cannot sign somebody out. */}
          <button
            type="button"
            onClick={() => void signOut()}
            className={cn(
              'mx-2.5 mt-2 flex h-11 shrink-0 items-center gap-2.5 border-t border-rail-divider',
              'pt-2 text-[13.5px] text-rail-ink hover:text-white md:hidden',
            )}
          >
            <LogOut className="size-[18px] shrink-0" aria-hidden="true" />
            Sign out
          </button>
        </nav>

        {/* min-w-0, or a wide table inside scrolls the whole page sideways instead of itself. */}
        <main
          className={cn(
            'min-w-0 flex-1 overflow-y-auto transition-[margin] duration-200',
            isExpanded ? 'md:ml-58' : 'md:ml-14',
          )}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * The signed-in person, and everything that is about them rather than about navigation.
 *
 * Sign out used to sit at the bottom of the rail. Under the single-rail model the rail is purely
 * navigation, so it moves here — which also gives the name, the email and the person's roles a
 * proper home instead of two lines of topbar text.
 */
function AccountMenu({ session }: { session: Session }) {
  const [isOpen, setOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Synchronising with document-level events, so an effect is the right tool rather than a handler
  // on an element.
  useEffect(() => {
    if (!isOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (containerRef.current?.contains(event.target as Node) === true) return;

      setOpen(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;

      setOpen(false);
      // Focus returns to the trigger, or a keyboard user is dropped at the top of the document.
      triggerRef.current?.focus();
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const roles = session.roles.map(shortRoleName);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Account"
        className={cn(
          'grid size-11 shrink-0 place-items-center rounded-full bg-avatar text-xs font-bold md:size-8',
          // White text, so this rides --avatar-bg rather than the neutral ramp — see tokens.css.
          'text-white',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        )}
      >
        {initials(session.name)}
      </button>

      {isOpen && (
        <div
          role="menu"
          className={cn(
            'absolute right-0 top-[calc(100%+6px)] z-50 w-56 rounded-lg border border-border',
            'bg-raised p-1.5 shadow-lg',
          )}
        >
          <div className="border-b border-border px-2 pb-2 pt-1.5">
            <p className="truncate text-[13px] font-semibold text-heading">
              {session.name ?? 'Signed in'}
            </p>
            {session.email !== null && (
              <p className="truncate text-xs text-muted">{session.email}</p>
            )}
            <p className="mt-1 text-[11px] text-muted">
              {roles.length > 0 ? roles.join(' · ') : 'No roles assigned'}
            </p>
          </div>

          {/* Phone only: the theme control folds in here, where the rows are full-width and clear
              the 44x44 touch minimum. */}
          <div className="mt-1.5 md:hidden">
            <ThemeMenuItems />
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut()}
            className={cn(
              'mt-0.5 flex min-h-11 w-full items-center gap-2.5 rounded px-2 text-[13px]',
              'text-neutral-strong hover:bg-row-hover hover:text-foreground md:min-h-0 md:py-2',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary',
            )}
          >
            <LogOut className="size-4 shrink-0" aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** The theme choice as menu rows, for the phone layout where the segmented control does not fit. */
function ThemeMenuItems() {
  const { preference, setPreference } = useTheme();

  return (
    <div role="group" aria-label="Colour theme">
      {THEME_OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          role="menuitemradio"
          aria-checked={preference === value}
          onClick={() => setPreference(value)}
          className={cn(
            'flex min-h-11 w-full items-center gap-2.5 rounded px-2 text-[13px]',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary',
            preference === value
              ? 'bg-primary-subtle font-semibold text-primary-strong'
              : 'text-neutral-strong hover:bg-row-hover hover:text-foreground',
          )}
        >
          <Icon className="size-4 shrink-0" aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Three explicit states rather than a two-way toggle, because 'auto' is a real preference and a
 * toggle that silently drops it is worse than one more control.
 */
function ThemeControl({ className }: { className?: string }) {
  const { preference, setPreference } = useTheme();

  return (
    <div
      className={cn('items-center gap-0.5 rounded-md bg-surface-2 p-0.5', className)}
      role="radiogroup"
      aria-label="Colour theme"
    >
      {THEME_OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={preference === value}
          aria-label={label}
          title={label}
          onClick={() => setPreference(value)}
          className={cn(
            'flex size-8 items-center justify-center rounded',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
            preference === value
              ? 'bg-surface text-foreground shadow-sm'
              : 'text-muted hover:text-foreground',
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
